import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Loader2, Save, RotateCcw, Lock, LayoutDashboard, Building2,
  Users, TrendingUp, Calendar, CheckSquare, ShoppingCart,
  FileText, BarChart2, FileDown, Shield, AlertTriangle, Eye, EyeOff, Target,
  Warehouse, Factory,
} from 'lucide-react';
import { toast } from 'sonner';

const SCOPE_LABELS = { own: 'Vista propria', role: 'Vista tutto il ruolo', all: 'Vista globale' };
const SCOPE_DESC = {
  own:  'Ogni utente vede solo i propri dati (aziende, ordini, attività assegnati a lui)',
  role: 'Ogni utente vede tutti i dati degli utenti con lo stesso ruolo',
  all:  'Tutti i dati del tenant — nessuna restrizione',
};

// ── Module definitions ──────────────────────────────────────────────────────────
const MODULES = [
  { key: 'dashboard',  label: 'Dashboard',              icon: LayoutDashboard, actions: ['view'],                          group: 'Core' },
  { key: 'companies',  label: 'Aziende',                icon: Building2,       actions: ['view','create','edit','delete'], group: 'Core' },
  { key: 'contacts',   label: 'Contatti',               icon: Users,           actions: ['view','create','edit','delete'], group: 'Core' },
  { key: 'pipeline',   label: 'Pipeline / Opportunità', icon: TrendingUp,      actions: ['view','create','edit','delete'], group: 'Core' },
  { key: 'calendar',   label: 'Calendario',             icon: Calendar,        actions: ['view','create','edit','delete'], group: 'Core' },
  { key: 'activities', label: 'Attività',               icon: CheckSquare,     actions: ['view','create','edit','delete'], group: 'Core' },
  { key: 'orders',     label: 'Ordini',                 icon: ShoppingCart,    actions: ['view','create','edit','delete'], group: 'Vendite' },
  { key: 'invoices',   label: 'Fatture',                icon: FileText,        actions: ['view','create','edit','delete'], group: 'Vendite' },
  { key: 'analytics',  label: 'Analisi Ordini',         icon: BarChart2,       actions: ['view'],                          group: 'Analisi' },
  { key: 'reports',    label: 'Report',                 icon: FileDown,        actions: ['view'],                          group: 'Analisi' },
  { key: 'forecast',   label: 'Forecast & Budget',      icon: Target,          actions: ['view','create','edit','delete'], group: 'Analisi' },
  { key: 'inventory',  label: 'Scorte',                 icon: Warehouse,       actions: ['view','create','edit','delete'], group: 'Operativo' },
  { key: 'production', label: 'Produzione',             icon: Factory,         actions: ['view','create','edit','delete'], group: 'Operativo' },
];

const ROLES = [
  { key: 'admin',         label: 'Amministratore',       color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',   locked: true },
  { key: 'sales_manager', label: 'Resp. Vendite',        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',       locked: false },
  { key: 'sales_rep',     label: 'Commerciale',          color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300', locked: false },
  { key: 'operator',      label: 'Operatore',            color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',   locked: false },
  { key: 'client',        label: 'Cliente',              color: 'bg-slate-100 text-slate-700 dark:bg-slate-700/30 dark:text-slate-300',   locked: false },
];

const ACTION_LABELS = { view: 'Vedi', create: 'Crea', edit: 'Modifica', delete: 'Elimina' };
const ACTION_COLORS = { view: 'text-blue-600', create: 'text-green-600', edit: 'text-amber-600', delete: 'text-red-500' };

// ── Permission cell ────────────────────────────────────────────────────────────
function PermCell({ module, role, perms, locked, onChange }) {
  const modPerms = perms?.[module.key] || {};
  if (locked) {
    return (
      <div className="flex flex-wrap gap-1 justify-center">
        {module.actions.map(action => (
          <div key={action} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/20">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            <span className="text-[10px] font-medium text-green-700 dark:text-green-400">{ACTION_LABELS[action]}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1 justify-center">
      {module.actions.map(action => {
        const enabled = modPerms[action] === true;
        // If view is off, disable other actions
        const disabled = action !== 'view' && !modPerms.view;
        return (
          <button
            key={action}
            onClick={() => !disabled && onChange(module.key, action, !enabled)}
            data-testid={`perm-${role}-${module.key}-${action}`}
            title={`${ACTION_LABELS[action]} — ${enabled ? 'Abilitato' : 'Disabilitato'}`}
            className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium transition-all ${
              disabled
                ? 'opacity-30 cursor-not-allowed border-border'
                : enabled
                ? 'border-transparent bg-primary/10 text-primary cursor-pointer hover:bg-primary/20'
                : 'border-border text-muted-foreground cursor-pointer hover:bg-muted'
            }`}
          >
            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${enabled && !disabled ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
            {ACTION_LABELS[action]}
          </button>
        );
      })}
    </div>
  );
}

// ── Role summary stats ─────────────────────────────────────────────────────────
function RoleSummary({ role, perms }) {
  const total    = MODULES.reduce((s, m) => s + m.actions.length, 0);
  const enabled  = MODULES.reduce((s, m) => s + m.actions.filter(a => perms?.[m.key]?.[a]).length, 0);
  const pct      = Math.round((enabled / total) * 100);
  return (
    <div className="text-center">
      <div className="text-xs font-bold text-foreground">{pct}%</div>
      <div className="w-12 h-1 rounded-full bg-muted mx-auto mt-0.5 overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function RolesPage() {
  const { reload: reloadPerms } = usePermissions();
  const [data, setData]             = useState(null);
  const [localRoles, setLocalRoles] = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [users, setUsers]           = useState([]);
  const [viewScopes, setViewScopes] = useState({});
  const [scopeDirty, setScopeDirty] = useState(false);
  const [activeTab, setActiveTab]   = useState('permissions');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [permsRes, usersRes, scopesRes] = await Promise.all([
        axios.get(`${API}/admin/permissions`),
        axios.get(`${API}/users`),
        axios.get(`${API}/admin/role-view-scopes`),
      ]);
      setData(permsRes.data);
      setLocalRoles(JSON.parse(JSON.stringify(permsRes.data.roles)));
      setUsers(usersRes.data);
      setViewScopes(scopesRes.data || {});
      setDirty(false);
      setScopeDirty(false);
    } catch { toast.error('Errore caricamento permessi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (role, module, action, value) => {
    setLocalRoles(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next[role]) next[role] = {};
      if (!next[role][module]) next[role][module] = {};
      next[role][module][action] = value;
      // If disabling view, disable all other actions too
      if (action === 'view' && !value) {
        const mod = MODULES.find(m => m.key === module);
        if (mod) mod.actions.forEach(a => { if (a !== 'view') next[role][module][a] = false; });
      }
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const tasks = [];
      if (dirty)       tasks.push(axios.put(`${API}/admin/permissions`, { roles: localRoles }));
      if (scopeDirty)  tasks.push(axios.put(`${API}/admin/role-view-scopes`, viewScopes));
      await Promise.all(tasks);
      toast.success('Salvato con successo');
      setDirty(false);
      setScopeDirty(false);
      await reloadPerms();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Ripristinare i permessi predefiniti per tutti i ruoli?')) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/permissions/reset`);
      toast.success('Permessi ripristinati ai valori predefiniti');
      await fetchData();
      await reloadPerms();
    } catch { toast.error('Errore ripristino'); }
    finally { setSaving(false); }
  };

  const groups = [...new Set(MODULES.map(m => m.group))];

  const hasDirty = dirty || scopeDirty;

  if (loading) return (
    <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={28} /></div>
  );

  const usersByRole = ROLES.reduce((acc, r) => {
    acc[r.key] = users.filter(u => u.role === r.key);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Ruoli e Permessi</h1>
          <p className="text-sm text-muted-foreground mt-1">Configura cosa può fare ogni ruolo e chi può vedere cosa</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} disabled={saving} data-testid="reset-defaults-btn">
            <RotateCcw size={14} className="mr-1.5" />Ripristina Default
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasDirty} data-testid="save-permissions-btn"
            className={hasDirty ? 'ring-2 ring-primary ring-offset-2' : ''}>
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            {hasDirty ? 'Salva Modifiche*' : 'Salvato'}
          </Button>
        </div>
      </div>

      {hasDirty && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm">
          <AlertTriangle size={16} className="flex-shrink-0" />
          Hai modifiche non salvate. Clicca "Salva Modifiche" per applicarle.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { key: 'users',       label: 'Utenti per Ruolo' },
          { key: 'scope',       label: 'Vista Dati' },
          { key: 'permissions', label: 'Permessi Moduli' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Utenti per Ruolo ─────────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLES.map(role => {
            const roleUsers = usersByRole[role.key] || [];
            return (
              <Card key={role.key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 ${role.color}`}>
                      {role.locked && <Lock size={10} />}{role.label}
                    </span>
                    <Badge variant="outline" className="text-xs">{roleUsers.length} utenti</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {roleUsers.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Nessun utente assegnato</p>
                  ) : (
                    <ul className="space-y-2">
                      {roleUsers.map(u => (
                        <li key={u._id || u.id} className="flex items-center gap-2 text-sm">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                            {(u.name || u.email || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{u.name || '—'}</p>
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          </div>
                          <div className={`ml-auto w-2 h-2 rounded-full flex-shrink-0 ${u.is_active !== false ? 'bg-green-500' : 'bg-muted-foreground/40'}`} title={u.is_active !== false ? 'Attivo' : 'Disattivo'} />
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── TAB: Vista Dati ────────────────────────────────────────────────── */}
      {activeTab === 'scope' && (
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/30 border text-sm text-muted-foreground">
            <strong>Vista Dati</strong>: configura per ogni ruolo se gli utenti vedono solo i propri dati, 
            tutti i dati del loro ruolo, o tutti i dati del tenant.
            Questo si applica ad Aziende, Contatti, Ordini, Attività, Opportunità, Fatture e Dashboard.
          </div>
          {ROLES.filter(r => !r.locked).map(role => {
            const currentScope = viewScopes[role.key] || 'own';
            return (
              <Card key={role.key}>
                <CardContent className="pt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2 min-w-[180px]">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${role.color}`}>{role.label}</span>
                      <span className="text-xs text-muted-foreground">({(usersByRole[role.key] || []).length} utenti)</span>
                    </div>
                    <div className="flex-1">
                      <Select value={currentScope}
                        onValueChange={v => { setViewScopes(s => ({ ...s, [role.key]: v })); setScopeDirty(true); }}>
                        <SelectTrigger className="w-full sm:w-64" data-testid={`scope-select-${role.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="own">Vista propria — solo i propri dati</SelectItem>
                          <SelectItem value="role">Vista ruolo — tutti i dati del ruolo</SelectItem>
                          <SelectItem value="all">Vista globale — tutti i dati del tenant</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">{SCOPE_DESC[currentScope]}</p>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      {currentScope === 'own' && <><EyeOff size={13} className="text-amber-500" /><span className="text-amber-600">Solo propri</span></>}
                      {currentScope === 'role' && <><Eye size={13} className="text-blue-500" /><span className="text-blue-600">Vista ruolo</span></>}
                      {currentScope === 'all'  && <><Eye size={13} className="text-green-500" /><span className="text-green-600">Globale</span></>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${ROLES.find(r=>r.key==='admin').color} inline-flex items-center gap-1`}>
                  <Lock size={10} />Amministratore
                </span>
                <span className="text-sm text-muted-foreground">Vede sempre tutti i dati — non modificabile</span>
                <div className="ml-auto flex items-center gap-1.5 text-xs font-medium text-green-600">
                  <Eye size={13} className="text-green-500" />Vista globale
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── TAB: Permessi Moduli ───────────────────────────────────────────── */}
      {activeTab === 'permissions' && (<>
      {/* Role legend */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map(r => (
          <div key={r.key} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${r.color}`}>
            {r.locked && <Lock size={10} />}
            {r.label}
            {r.locked && <span className="ml-0.5 opacity-70">— bloccato</span>}
          </div>
        ))}
      </div>

      {/* Permission matrix */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-muted/60 border-b">
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs w-48">Modulo</th>
                {ROLES.map(role => (
                  <th key={role.key} className="px-2 py-3 text-center min-w-[140px]">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${role.color}`}>
                        {role.locked && <Lock size={9} />}{role.label}
                      </span>
                      <RoleSummary role={role.key} perms={localRoles[role.key]} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <React.Fragment key={group}>
                  <tr className="bg-muted/20">
                    <td colSpan={ROLES.length + 1} className="px-4 py-1.5">
                      <span className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground">{group}</span>
                    </td>
                  </tr>
                  {MODULES.filter(m => m.group === group).map((module, idx) => {
                    const Icon = module.icon;
                    return (
                      <tr key={module.key} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/5'}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                              <Icon size={14} className="text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium text-sm leading-tight">{module.label}</p>
                              <p className="text-[10px] text-muted-foreground">{module.actions.map(a => ACTION_LABELS[a]).join(' · ')}</p>
                            </div>
                          </div>
                        </td>
                        {ROLES.map(role => (
                          <td key={role.key} className="px-2 py-3 text-center">
                            <PermCell
                              module={module}
                              role={role.key}
                              perms={localRoles[role.key]}
                              locked={role.locked}
                              onChange={(mod, action, val) => handleChange(role.key, mod, action, val)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-primary" />Permesso attivo</div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-muted-foreground/40" />Permesso disattivo</div>
        <div className="flex items-center gap-1.5"><Lock size={11} />Ruolo bloccato</div>
        <span className="text-amber-600 dark:text-amber-400">* Le modifiche si applicano alla prossima navigazione degli utenti</span>
      </div>
      </>)}
    </div>
  );
}
